import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import './Auth.css';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  // Surligne les deux champs sans révéler lequel est faux (email et mot de
  // passe partagent volontairement le même message générique — sinon on
  // permettrait de deviner quels emails sont inscrits sur la plateforme)
  const [credentialsError, setCredentialsError] = useState(false);

  const { login } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const passwordResetSuccess = location.state?.passwordResetSuccess;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setCredentialsError(false);

    const result = await login(email, password);

    if (result.success) {
      // Rediriger vers /admin pour les admin/manager, sinon /dashboard
      if (result.user && ['admin', 'manager'].includes(result.user.role)) {
        navigate('/admin');
      } else {
        navigate('/dashboard');
      }
    } else if (result.requiresEmailVerification) {
      navigate('/verify-email', { state: { email: result.email } });
    } else if (result.requiresAdminCode) {
      navigate('/verify-email', { state: { email: result.email, mode: 'admin' } });
    } else if (result.error === 'Identifiants incorrects') {
      // Message générique volontaire (email ET mot de passe surlignés
      // ensemble, sans dire lequel est faux — anti-énumération de comptes)
      setCredentialsError(true);
      setError(t('auth.invalidCredentials'));
    } else {
      setError(result.error);
    }

    setLoading(false);
  };

  const handleEmailChange = (e) => {
    setEmail(e.target.value);
    if (credentialsError) setCredentialsError(false);
  };

  const handlePasswordChange = (e) => {
    setPassword(e.target.value);
    if (credentialsError) setCredentialsError(false);
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="auth-header">
          <h1>📈 CryptoBot</h1>
          <p>{t('auth.loginTitle')}</p>
        </div>

        {passwordResetSuccess && (
          <div className="verify-resend-success">{t('auth.passwordResetSuccess')}</div>
        )}
        {error && <div className="auth-error">{error}</div>}

        <form onSubmit={handleSubmit} className="auth-form">
          <div className={`form-group ${credentialsError ? 'has-error' : ''}`}>
            <label htmlFor="email">{t('auth.email')}</label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={handleEmailChange}
              placeholder="your@email.com"
              className={credentialsError ? 'input-error' : ''}
              required
            />
          </div>

          <div className={`form-group ${credentialsError ? 'has-error' : ''}`}>
            <label htmlFor="password">{t('auth.password')}</label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={handlePasswordChange}
              placeholder="••••••••"
              className={credentialsError ? 'input-error' : ''}
              required
            />
            <Link to="/forgot-password" className="forgot-password-link">{t('auth.forgotPassword')}</Link>
          </div>

          <button type="submit" className="auth-button" disabled={loading}>
            {loading ? t('auth.loggingIn') : t('auth.login')}
          </button>
        </form>

        <div className="auth-footer">
          <p>
            {t('auth.noAccount')}{' '}
            <Link to="/register">{t('auth.createAccount')}</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
